import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useFormValidation } from "../../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../Common/FormElements/InputField";
import { SelectField } from "../../Common/FormElements/SelectField";
import { fetchTagDropdowns } from "../../../services/tagService";

import {
  fetchTagCollectionById,
  updateTagCollection,
} from "../../../services/tagCollectionService";
const UpdateTagCollection = () => {
  const parseMultiSelectValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };
  // Helper function to convert text to URL slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple consecutive hyphens with single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
  };

  const { id } = useParams();

  // ✅ Move all hooks to the top
  const navigate = useNavigate();
  const PageLevelAccessurl = "tag-collection/add";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const collectionNameRef = useRef(null);
  const collectionTagURLRef = useRef(null);
  const mappedTagIdsRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    Id: "",
    CollectionName: "",
    CollectionTagURL: "",
    MappedTagIds: "",
    Published: false,
    Featured: false,
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [productTags, setProductTags] = useState([]);

  const loadProductTags = async () => {
    try {
      const productTagData = await fetchTagDropdowns();
      if (
        productTagData &&
        productTagData.result &&
        productTagData.result.length > 0
      ) {
        setProductTags(productTagData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };

  useEffect(() => {
    loadProductTags();
    const fetchData = async () => {
      try {
        const response = await fetchTagCollectionById(id);
        if (response && response.isSuccess && response.result) {
          setFormValues({
            Id: response.result.id,
            CollectionName: response.result.collectionName,
            CollectionTagURL: response.result.collectionTagURL,
            MappedTagIds: parseMultiSelectValue(response.result.mappedTagIds),
            Published: response.result.published,
            Featured: response.result.featured,
          });
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  }, [id]);

  // ✅ Register all fields
  useEffect(() => {
    registerField("CollectionName", collectionNameRef, "Collection Name");
    registerField("CollectionTagURL", collectionTagURLRef, "Collection URL");
    registerField("MappedTagIds", mappedTagIdsRef, "Mapped Tags");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };

      // Auto-populate TagURL when TagName changes
      // Only auto-populate if URL field is empty or was previously auto-generated
      if (id === "CollectionName") {
        // Check if URL field is empty or matches the slug of previous barnd name
        const previousSlug = convertToSlug(prev.CollectionName);
        const shouldAutoPopulate =
          !prev.CollectionTagURL || prev.CollectionTagURL === previousSlug;

        if (shouldAutoPopulate) {
          newValues.CollectionTagURL = convertToSlug(inputValue);
        }
      }

      return newValues;
    });
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      collectionNameRef,
      collectionTagURLRef,
      mappedTagIdsRef,
    };

    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      var responseData = await updateTagCollection(formValues);

      // ✅ Reset all fields using refs
      // resetAllFields();

      // ✅ Clear form errors
      clearErrors();

      if (responseData?.isSuccess) {
        toast.success(responseData?.statusMessage);
      } else {
        toast.error(responseData?.statusMessage);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <ComponentHeader title="Update Tag Collection" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-8 col-lg-8">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Update Tag Collection</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="Published" className="form-label pe-3">
                    Published Tag Collection
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="Published"
                      name="Published"
                      checked={formValues.Published}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="Published">
                      {formValues.Published ? "Yes" : "No"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-12 col-md-6 col-sm-12">
                    <InputField
                      ref={collectionNameRef}
                      label="Collection Name"
                      id="CollectionName"
                      value={formValues.CollectionName}
                      placeholder="Enter your Collection Name"
                      onChange={handleInputChange}
                      required={true}
                      validationType="alphanumeric"
                      maxLength={300}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <InputField
                      ref={collectionTagURLRef}
                      label="Collection URL"
                      id="CollectionTagURL"
                      value={formValues.CollectionTagURL}
                      placeholder="Auto generated URL"
                      onChange={handleInputChange}
                      maxLength={300}
                      required={true}
                      validationType="url"
                    />
                  </div>
                </div>

                <div className="col-lg-6 mb-3 mt-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="Featured" className="form-label pe-3">
                      Featured Tag
                    </label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="Featured"
                        name="Featured"
                        checked={formValues.Featured}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="Featured">
                        {formValues.Featured ? "Yes" : "No"}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="text-start">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-4 col-lg-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Tags <span className="required-field">*</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <SelectField
                  ref={mappedTagIdsRef}
                  label="Mapped Tags"
                  id="MappedTagIds"
                  value={formValues.MappedTagIds}
                  onChange={handleInputChange}
                  options={productTags}
                  optionValueKey="id"
                  optionLabelKey="tagName"
                  placeholder="Select Product Tags"
                  required={false}
                  isMulti={true}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default UpdateTagCollection;
